import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { motion } from 'framer-motion';
import {
  UserIcon,
  EnvelopeIcon,
  ShieldCheckIcon,
  CalendarIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  KeyIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import axios from 'axios';                                     
import api from '../../services/api';
import { currentAge, ageFromBirthDate, birthDateBounds, birthDateError } from '../../utils/age';
import ProfileSurveyCard from '../survey/ProfileSurveyCard';
const Profile = () => {
  const { user, updateUser, logout } = useAuth();
  const [isEditing, setIsEditing] = useState(true);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const { apiUrl, hrApiUrl } = window['Config'] || {};
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    birth_date: '',
    region: '',
    degree: '',
    job_role: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});

  const degreeOptions = ['MD', 'MBBS', 'DO', 'PhD', 'MD-PhD', 'MS', 'MPH', 'RN', 'NP', 'PA', 'MBA', 'Other'];
  const jobOptions = [
    'Transplant Surgeon', 'Transplant Nephrologist', 'Transplant Coordinator',
    'Clinician / Physician', 'Nurse', 'Researcher', 'Data Scientist / Analyst',
    'Student / Trainee', 'Other'
  ];
  const usStates = ['Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','District of Columbia','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming'];

  useEffect(() => {
    if (user) {
      setFormData({
        ...formData,
        username: user.username || '',
        email: user.email || '',
        birth_date: user.birth_date || '',
        region: user.region || '',
        degree: user.degree || '',
        job_role: user.job_role || '',
      });
      fetchUserStats();
      fetchChatHistory();
    }
  }, [user]);

  const fetchUserStats = async () => {
    try {
      const response = await axios.get('/api/chat/my-limits', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchChatHistory = async () => {
    try {
      const response = await axios.get('/api/chat/history?limit=5', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setChatHistory(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching chat history:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Optional here (older accounts may not have one yet), but must be valid.
    if (formData.birth_date) {
      const birthError = birthDateError(formData.birth_date);
      if (birthError) newErrors.birth_date = birthError;
    }
    
    if (formData.newPassword) {
      if (formData.newPassword.length < 6) {
        newErrors.newPassword = 'Password must be at least 6 characters';
      }
      if (!formData.currentPassword) {
        newErrors.currentPassword = 'Current password is required to set new password';
      }
      if (formData.newPassword !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const updateData = {
        username: formData.username,
        email: formData.email,
        birth_date: formData.birth_date,
        region: formData.region,
        degree: formData.degree,
        job_role: formData.job_role,
      };
      
      if (formData.newPassword) {
        updateData.currentPassword = formData.currentPassword;
        updateData.newPassword = formData.newPassword;
      }

      const response = await api.put('/update-profile', updateData);

      if (response.data.success) {
        updateUser(response.data.user);
        toast.success('Profile updated successfully');
        setIsEditing(false);
        setFormData({
          ...formData,
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getUsagePercentage = (used, limit) => {
    return Math.min((used / limit) * 100, 100);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>
          <p className="text-gray-600 mt-2">Manage your account settings and view your activity</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Profile Info */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl shadow-lg overflow-hidden sticky top-24"
            >
              {/* Profile Header */}
              <div className="bg-gradient-to-r from-primary-600 to-primary-500 px-6 py-8 text-center">
                <div className="inline-block">
                  <div className="w-28 h-28 rounded-full bg-white shadow-lg flex items-center justify-center mx-auto border-4 border-white">
                    <span className="text-4xl font-bold text-primary-600">
                      {user?.username?.[0]?.toUpperCase() || 'U'}
                    </span>
                  </div>
                </div>
                <h2 className="text-white text-xl font-semibold mt-4">{user?.username}</h2>
                <p className="text-primary-100 text-sm mt-1">{user?.email}</p>
                <div className="mt-3">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                    user?.role === 'admin' 
                      ? 'bg-purple-200 text-purple-800' 
                      : 'bg-green-200 text-green-800'
                  }`}>
                    <ShieldCheckIcon className="h-3 w-3 mr-1" />
                    {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}
                  </span>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ml-2 ${
                    (user?.role === 'admin' || user?.is_approved)
                      ? 'bg-green-200 text-green-800'
                      : 'bg-yellow-200 text-yellow-800'
                  }`}>
                    {user?.role === 'admin' ? 'Active' : (user?.is_approved ? 'Approved' : 'Pending Approval')}
                  </span>
                </div>
              </div>

              {/* Profile Info */}
              <div className="px-6 py-6 space-y-4">
                <div className="flex items-center text-gray-600">
                  <CalendarIcon className="h-5 w-5 mr-3 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Joined</p>
                    <p className="text-sm font-medium">{formatDate(user?.created_at)}</p>
                  </div>
                </div>
                <div className="flex items-center text-gray-600">
                  <UserIcon className="h-5 w-5 mr-3 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Age</p>
                    <p className="text-sm font-medium">
                      {currentAge(user) ?? 'Not set'}
                      {user?.birth_date && <span className="ml-1 text-xs font-normal text-gray-400">(from date of birth)</span>}
                    </p>
                  </div>
                </div>
                <div className="flex items-center text-gray-600">
                  <ClockIcon className="h-5 w-5 mr-3 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Last Login</p>
                    <p className="text-sm font-medium">{user?.last_login ? formatDate(user.last_login) : 'Never'}</p>
                  </div>
                </div>
                <div className="flex items-center text-gray-600">
                  <ChatBubbleLeftRightIcon className="h-5 w-5 mr-3 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Total Messages</p>
                    <p className="text-sm font-medium">{stats?.usage?.total || 0}</p>
                  </div>
                </div>
              </div>

              {/* Edit Button */}
              {!isEditing && (
                <div className="px-6 pb-6">
                  <button
                    onClick={() => setIsEditing(true)}
                    className="w-full flex items-center justify-center px-4 py-2 border border-primary-600 text-primary-600 rounded-lg hover:bg-primary-50 transition-colors duration-200"
                  >
                    <PencilIcon className="h-4 w-4 mr-2" />
                    Edit Profile
                  </button>
                </div>
              )}
            </motion.div>
          </div>

          {/* Right Column - Edit Form & Stats */}
          <div className="lg:col-span-2 space-y-6">
            {/* Edit Profile Form */}
            {isEditing && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl shadow-lg p-6"
              >
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-semibold text-gray-900">Edit Profile</h3>
                  {/* <button
                    onClick={() => setIsEditing(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button> */}
                </div>

                <form onSubmit={handleUpdateProfile} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name
                    </label>
                    <div className="relative">
                      <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        name="username"
                        value={formData.username}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <div className="relative">
                      <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date of birth</label>
                    <input type="date" name="birth_date" {...birthDateBounds()} value={formData.birth_date} onChange={handleInputChange}
                      className={`w-full px-4 py-2 border ${errors.birth_date ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent`} />
                    {errors.birth_date ? (
                      <p className="mt-1 text-sm text-red-600">{errors.birth_date}</p>
                    ) : ageFromBirthDate(formData.birth_date) !== null ? (
                      <p className="mt-1 text-xs text-gray-500">Age {ageFromBirthDate(formData.birth_date)} — updates automatically on your birthday.</p>
                    ) : user?.age != null && (
                      <p className="mt-1 text-xs text-amber-700">
                        Your age on file is {user.age} (entered at signup). Add your date of birth so it stays up to date.
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                    <select name="region" value={formData.region} onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent">
                      <option value="">Select state…</option>
                      {usStates.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Degree</label>
                    <select name="degree" value={formData.degree} onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent">
                      <option value="">Select degree…</option>
                      {degreeOptions.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Job role</label>
                    <select name="job_role" value={formData.job_role} onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent">
                      <option value="">Select role…</option>
                      {jobOptions.map((j) => <option key={j} value={j}>{j}</option>)}
                    </select>
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                      <KeyIcon className="h-5 w-5 mr-2" />
                      Change Password (Optional)
                    </h4>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Current Password
                        </label>
                        <input
                          type="password"
                          name="currentPassword"
                          value={formData.currentPassword}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Enter current password"
                        />
                        {errors.currentPassword && (
                          <p className="mt-1 text-sm text-red-600">{errors.currentPassword}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          New Password
                        </label>
                        <input
                          type="password"
                          name="newPassword"
                          value={formData.newPassword}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Enter new password"
                        />
                        {errors.newPassword && (
                          <p className="mt-1 text-sm text-red-600">{errors.newPassword}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Confirm New Password
                        </label>
                        <input
                          type="password"
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Confirm new password"
                        />
                        {errors.confirmPassword && (
                          <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-3 pt-4">
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-primary-600 text-white py-2 rounded-lg hover:bg-primary-700 transition-colors duration-200 disabled:opacity-50"
                    >
                      {loading ? (
                        <ArrowPathIcon className="animate-spin h-5 w-5 mx-auto" />
                      ) : (
                        'Save Changes'
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsEditing(false)}
                      className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </motion.div>
            )}

            {/* Survey answers (always the latest submitted copy) */}
            <ProfileSurveyCard />

            {/* Usage Statistics */}
            {stats && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-2xl shadow-lg p-6"
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                  <ChartBarIcon className="h-6 w-6 mr-2 text-primary-600" />
                  Usage Statistics
                </h3>

                <div className="space-y-6">
                  {/* Daily Usage */}
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Daily Messages</span>
                      <span className="text-sm text-gray-600">
                        {stats.usage?.today || 0} / {stats.limits?.daily || 50}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${getUsagePercentage(stats.usage?.today || 0, stats.limits?.daily || 50)}%` }}
                      />
                    </div>
                  </div>

                  {/* Weekly Usage */}
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Weekly Messages</span>
                      <span className="text-sm text-gray-600">
                        {stats.usage?.week || 0} / {stats.limits?.weekly || 350}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${getUsagePercentage(stats.usage?.week || 0, 350)}%` }}
                      />
                    </div>
                  </div>

                  {/* Monthly Usage */}
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Monthly Messages</span>
                      <span className="text-sm text-gray-600">
                        {stats.usage?.month || 0} / {stats.limits?.monthly || 500}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${getUsagePercentage(stats.usage?.month || 0, stats.limits?.monthly || 500)}%` }}
                      />
                    </div>
                  </div>

                  {/* Total Usage */}
                  <div className="pt-4 border-t">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm text-gray-500">Total Messages Sent</p>
                        <p className="text-2xl font-bold text-gray-900">{stats.usage?.total || 0}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500">Account Status</p>
                        <p className={`text-sm font-medium ${stats.blocked ? 'text-red-600' : 'text-green-600'}`}>
                          {stats.blocked ? 'Blocked' : 'Active'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Recent Chat History */}
            {chatHistory.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-2xl shadow-lg p-6"
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <DocumentTextIcon className="h-6 w-6 mr-2 text-primary-600" />
                  Recent Conversations
                </h3>

                <div className="space-y-3">
                  {chatHistory.slice(0, 5).map((chat, index) => (
                    <div key={chat.id} className="border-b border-gray-100 last:border-0 pb-3 last:pb-0">
                      <p className="text-sm text-gray-600 line-clamp-2">
                        <span className="font-medium text-gray-900">You:</span> {chat.message}
                      </p>
                      {chat.response && (
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                          <span className="font-medium text-gray-900">AI:</span> {chat.response}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(chat.created_at).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>

                {chatHistory.length > 0 && (
                  <button
                    onClick={() => window.location.href = '/chat'}
                    className="mt-4 text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    View all conversations →
                  </button>
                )}
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;